import { ReactComponent as EditIcon } from "../../../assets/svgIcon/shipment/Edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/svgIcon/shipment/DeleteOutlined.svg";
import {
  Actions,
  Badge,
  CardBody,
  CardMain,
  DueBadge,
  EntryLine,
  GroupHeading,
  ListRoot,
  Meta,
  NoteCard,
  RecordChip,
  ShipmentLink,
  TaskCheckbox,
} from "./NotesList.styles";
import { parseNoteCommands } from "./parseNoteCommands";
import { taskActivity } from "./taskActivity";
import { absoluteDueLabel, dueUrgency, groupTasksByDue, relativeDueLabel } from "./taskDue";
import { splitMentionedContent } from "./taskMentions";

function recordTypeLabel(type) {
  if (type === "purchase_order") return "Purchase order";
  if (type === "transfer_order") return "Transfer order";
  return "Shipment";
}

function taggedRecords(item) {
  if (item.records?.length) return item.records;
  return item.primary_record ? [item.primary_record] : [];
}

function displayContent(content) {
  return parseNoteCommands(content).content || content;
}

function RecordChipContent({ record, showRecordLinks, onRecordOpen }) {
  const label = `${recordTypeLabel(record.type)} ${record.name}`;
  if (showRecordLinks && record.detail_url) {
    return (
      <ShipmentLink to={record.detail_url} onClick={onRecordOpen}>
        {label}
      </ShipmentLink>
    );
  }
  return label;
}

function TaskRow({
  item,
  handleCheckboxChange,
  toggleEditBox,
  openEditBox,
  deleteNote,
  showRecordLinks,
  onRecordOpen,
  compactDueDate,
}) {
  const isTask = item.is_task;
  const overdue = dueUrgency(item.due_date) === "overdue" && isTask && !item.is_done;
  const records = taggedRecords(item);
  const dueTitle = absoluteDueLabel(item.due_date);
  const dueText = relativeDueLabel(item.due_date);
  const activity = taskActivity(item);
  const { segments, unusedRecords } = splitMentionedContent(displayContent(item.content), records);
  const cardClassName = item.is_done ? "struck" : isTask ? "" : "note";

  return (
    <NoteCard>
      <TaskCheckbox
        type="checkbox"
        checked={item.is_done}
        onChange={event => handleCheckboxChange(event, item.id)}
        aria-label={`Mark ${isTask ? "task" : "note"} ${item.is_done ? "incomplete" : "complete"}`}
      />
      <CardBody>
        <EntryLine>
          {segments.map((segment, index) => {
            if (segment.type === "record") {
              const record = segment.record;
              return (
                <RecordChip key={`${record.type}-${record.id ?? record.name}-${index}`}>
                  <RecordChipContent
                    record={record}
                    showRecordLinks={showRecordLinks}
                    onRecordOpen={onRecordOpen}
                  />
                </RecordChip>
              );
            }
            if (!segment.value) return null;
            return (
              <CardMain
                key={`text-${index}`}
                type="button"
                className={cardClassName}
                onClick={() => toggleEditBox(item)}
              >
                {segment.value}
              </CardMain>
            );
          })}
          {showRecordLinks &&
            unusedRecords.map(record => (
              <RecordChip key={`${record.type}-${record.id ?? record.name}-trail`}>
                <RecordChipContent
                  record={record}
                  showRecordLinks={showRecordLinks}
                  onRecordOpen={onRecordOpen}
                />
              </RecordChip>
            ))}
          {item.due_date && !compactDueDate && (
            <DueBadge className={overdue ? "overdue" : ""} title={dueTitle}>
              {dueText}
            </DueBadge>
          )}
        </EntryLine>
        <Meta>
          {activity.line}{" "}
          {!compactDueDate && <Badge $isTask={isTask}>{isTask ? "Task" : "Note"}</Badge>}
          {compactDueDate && item.due_date && (
            <DueBadge className={`subtle${overdue ? " overdue" : ""}`} title={dueTitle}>
              {dueText}
            </DueBadge>
          )}
        </Meta>
      </CardBody>
      <Actions className="entry-actions">
        <button
          type="button"
          title="Edit"
          aria-label="Edit entry"
          onClick={() => openEditBox(item)}
        >
          <EditIcon />
        </button>
        <button
          type="button"
          title="Delete"
          aria-label="Delete entry"
          onClick={() => deleteNote(item.id)}
        >
          <DeleteIcon />
        </button>
      </Actions>
    </NoteCard>
  );
}

export const NotesList = ({
  listOfItem,
  handleCheckboxChange,
  toggleEditBox,
  openEditBox,
  deleteNote,
  showRecordLinks = false,
  onRecordOpen,
  compactDueDate = false,
  groupByDue = false,
}) => {
  const groups = groupByDue
    ? groupTasksByDue(listOfItem)
    : [{ key: "all", label: null, items: listOfItem }];

  return (
    <ListRoot>
      {groups.map(group => [
        group.label ? <GroupHeading key={`${group.key}-heading`}>{group.label}</GroupHeading> : null,
        ...group.items.map(item => (
          <TaskRow
            key={item.id}
            item={item}
            handleCheckboxChange={handleCheckboxChange}
            toggleEditBox={toggleEditBox}
            openEditBox={openEditBox}
            deleteNote={deleteNote}
            showRecordLinks={showRecordLinks}
            onRecordOpen={onRecordOpen}
            compactDueDate={compactDueDate}
          />
        )),
      ])}
    </ListRoot>
  );
};
